import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Spinner } from '../../../shared/spinner/Spinner';
import { fetchAllPlaygroundQuestionsAndAnswersStarted, getCurrentSelectedQueries, getCustomQueries, getIsFetchingAllPreviousQuestionsAndAnswers, getPreviouslyAskedFilteredQueries, getPreviouslyAskedQueriesSearchTerm, getSelectedPreviousQuestionsTab, MLTestDocument, PreviousQuestionsTab, QuestionsAndAnswers, setSelectedPreviousQuestionsTab, togglePreviouslyAskedQuestion, updatePreviouslyAskedQueriesSearch } from './store';
import styles from './AIPlayground.module.scss';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { Text } from '../../../shared/text/Text';
import { AIPreviousQuestionsAndAnswers } from './AIPreviousQuestionsAndAnswers';
import { WizardTab } from '../../../shared/wizard/WizardTab';

const { lightGrey } = styles;

export interface AIPlaygroundPreviouslyAskedQuestionsProps {
    mlTestDocument: MLTestDocument;
}

export const AIPlaygroundPreviouslyAskedQuestions: React.FC<AIPlaygroundPreviouslyAskedQuestionsProps> = ({ mlTestDocument }) => {
    const dispatch = useAppDispatch();

    useFetchStarted([fetchAllPlaygroundQuestionsAndAnswersStarted(mlTestDocument!.documentNameId!)]);

    const isLoading = useAppSelector(getIsFetchingAllPreviousQuestionsAndAnswers);
    const selectedQueries = useAppSelector(getCurrentSelectedQueries);
    const customQueries = useAppSelector(getCustomQueries);
    const previouslyAskedFilteredQueries = useAppSelector(getPreviouslyAskedFilteredQueries);
    const previouslyAskedQueriesSearchTerm = useAppSelector(getPreviouslyAskedQueriesSearchTerm);
    const selectedTab = useAppSelector(getSelectedPreviousQuestionsTab);

    const correctQueries = useMemo(() => previouslyAskedFilteredQueries.filter(({ correct }) => correct), [previouslyAskedFilteredQueries]);
    const incorrectQueries = useMemo(() => previouslyAskedFilteredQueries.filter(({ correct }) => !correct), [previouslyAskedFilteredQueries]);

    const toggleQuestion = useCallback((question: QuestionsAndAnswers) => dispatch(togglePreviouslyAskedQuestion(question)), [dispatch]);

    const isChecked = useCallback((queryKey: string) => selectedQueries.map(({ queryKey }) => queryKey).includes(queryKey), [selectedQueries]);

    const searchPreviouslyAskedQueries = useCallback((e: React.ChangeEvent<HTMLInputElement>) => dispatch(updatePreviouslyAskedQueriesSearch(e.target.value)), [dispatch]);

    const aiPlaygroundTabs = Object.values(PreviousQuestionsTab);
    const selectTab = useCallback((tab: PreviousQuestionsTab) => dispatch(setSelectedPreviousQuestionsTab(tab)), [dispatch]);

    const wizardTabs = useMemo(() => aiPlaygroundTabs.map(tab => ({
        label: tab,
        selected: tab === selectedTab,
        onClick: () => selectTab(tab),
        total: customQueries.length,
        correct: correctQueries.length,
        incorrect: incorrectQueries.length,
        previouslyAskedQueriesSearchTerm: previouslyAskedQueriesSearchTerm.length > 0
    })), [selectTab, selectedTab, aiPlaygroundTabs, customQueries, correctQueries, incorrectQueries, previouslyAskedQueriesSearchTerm]);

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <div className={styles.previouslyAskedQuestionsWrapper}>
            <div className={styles.previousQuestionsSearchWrapper}>
                <Text
                    onChange={searchPreviouslyAskedQueries}
                    value={previouslyAskedQueriesSearchTerm}
                    maxLength={256}
                    testId='previously-asked-query-search'
                    placeholder='Search...'
                    marginBottom='0px'
                    borderColour={lightGrey}
                    width='30%'
                />
                <div className={styles.playgroundTabsWrapper}>
                    {wizardTabs.map(({ label, onClick, selected, total, correct, incorrect, previouslyAskedQueriesSearchTerm }) => {
                        const numerator = label === PreviousQuestionsTab.CORRECT ? correct : incorrect;
                        return <WizardTab key={label} label={label} onClick={onClick} selected={selected} testId='ai-playground' denominator={total} numerator={numerator} showNumberFraction={previouslyAskedQueriesSearchTerm} />;
                    })}
                </div>
            </div>
            {previouslyAskedFilteredQueries.length === 0 ?
                <div className={styles.allPreviousQuestionsEmpty}>No questions match this search input</div> :
                <div className={styles.allPreviousQuestionsWrapper}>
                    {selectedTab === PreviousQuestionsTab.CORRECT ?
                        <Scrollable>
                            {correctQueries.length === 0 ? <div className={styles.previousQuestionsSectionEmpty}>No questions were found for this section</div> :
                                correctQueries.map(query => (
                                    <AIPreviousQuestionsAndAnswers
                                        key={query.queryKey}
                                        query={query}
                                        toggleQuestion={toggleQuestion}
                                        checked={isChecked(query.queryKey)}
                                    />
                                ))}
                        </Scrollable>
                        : null}
                    {selectedTab === PreviousQuestionsTab.INCORRECT ?
                        <Scrollable>
                            {incorrectQueries.length === 0 ? <div className={styles.previousQuestionsSectionEmpty}>No questions were found for this section</div> :
                                incorrectQueries.map(query => (
                                    <AIPreviousQuestionsAndAnswers
                                        key={query.queryKey}
                                        query={query}
                                        toggleQuestion={toggleQuestion}
                                        checked={isChecked(query.queryKey)}
                                    />
                                ))}
                        </Scrollable>
                        : null}
                </div>}
        </div>
    );
};
